import { useTourRun } from '@flobi/asset-library/tours';
import type { GenerationClient, Quote, Result, Run, RunFrame, RunFeed } from '../../editor/ai/contracts';

/**
 * Free runs in the tour (30 Oct 2026, the Learn formula): while "Make it with AI" runs, Generate
 * spends nothing. The panel plays a real run's states (queued, generating, saving) and then a
 * picture made once for the tour lands where a real result would, as a real picture in the file
 * (uploaded the way a canvas reference is, so it has its own media asset). The price reads
 * "Free in the tour", and the prompt starts filled with the words that picture was made from.
 * Video, the picture tools (Edit with AI), and everything outside the tour stay real.
 */
export const FREE_IN_TOUR = 'Free in the tour';
export const TOUR_PROMPT = 'A frosted violet glass bottle with a pink cap on a lilac pedestal, soft studio light';
const TOUR_PICTURE = '/learn/violet-bottle.jpg';
const TOUR_TOUR = 'ai';
/** How long a free run takes, as the progress shows it. */
const TAKES_MS = 6000;

const isTourRun = (id: string) => id.startsWith('tour-');

/** True while the AI tour is on screen (not paused): the editor swaps in the free client then. */
export function useFreeGeneration(): boolean {
  const run = useTourRun();
  return Boolean(run && !run.paused && run.tour === TOUR_TOUR);
}

// The free runs of this visit and who hears their frames, kept outside one client: the client is
// swapped back to the real one when the tour ends, and a free run already going still lands.
const runs = new Map<string, { startedAt: number; result: Promise<Result> }>();
const listeners = new Set<(frame: RunFrame) => void>();
const tell = (frame: RunFrame) => listeners.forEach((fn) => fn(frame));

async function upload(real: GenerationClient): Promise<Result> {
  const response = await fetch(TOUR_PICTURE);
  if (!response.ok) throw Error('The tour’s picture is missing.');
  const picture = await real.reference(await response.blob(), 'Violet bottle.jpg', 'canvas');
  if (!picture.mediaAssetId) throw Error('The tour’s picture could not be saved.');
  return { mediaAssetId: picture.mediaAssetId, url: picture.url, width: picture.width, height: picture.height, mime: picture.mime, name: 'Violet bottle' };
}

/**
 * `real` with Generate made free (`free`, the AI tour is on), or as it is. Either way a free run
 * already started is answered here, never sent to the server.
 */
export function tourGenerationClient(real: GenerationClient, free: boolean): GenerationClient {
  return {
    ...real,
    async quote(request) {
      if (!free || request.kind !== 'image') return real.quote(request);
      return { credits: 0, seconds: TAKES_MS / 1000, label: FREE_IN_TOUR } satisfies Quote;
    },
    starterPrompt: () => (free ? TOUR_PROMPT : null),
    async generate(request) {
      if (!free || request.kind !== 'image') return real.generate(request);
      const id = `tour-${crypto.randomUUID()}`;
      const result = upload(real);
      result.catch(() => {});
      runs.set(id, { startedAt: Date.now(), result });
      // The frames a real run sends: generating, then saving, then done.
      [0.15, 0.45, 0.75].forEach((at) => setTimeout(() => tell({ id, event: 'progress', stage: 'generating', progress: Math.round(at * 100) }), at * TAKES_MS));
      setTimeout(() => tell({ id, event: 'progress', stage: 'uploading', progress: 95 }), TAKES_MS * 0.92);
      setTimeout(() => void result.then(() => tell({ id, event: 'completed' }), () => tell({ id, event: 'failed' })), TAKES_MS);
      return { id, status: 'queued', results: [] } satisfies Run;
    },
    async get(id) {
      const run = runs.get(id);
      if (!run) return isTourRun(id) ? { id, status: 'failed', results: [], userMessage: 'This free run ended with the page. Nothing was charged.' } : real.get(id);
      const elapsed = Date.now() - run.startedAt;
      if (elapsed < TAKES_MS) return { id, status: 'processing', results: [], progress: Math.min(95, Math.round((elapsed / TAKES_MS) * 100)) };
      try {
        return { id, status: 'completed', results: [await run.result] };
      } catch (problem) {
        return { id, status: 'failed', results: [], userMessage: `${problem instanceof Error ? problem.message : 'It didn’t land.'} Nothing was charged.` };
      }
    },
    async cancel(id) {
      if (!isTourRun(id)) return real.cancel(id);
      runs.delete(id);
      return { cancelled: true, stopped: true };
    },
    feed(onFrame, onConnection) {
      listeners.add(onFrame);
      const inner: RunFeed | null = real.feed ? real.feed(onFrame, onConnection) : (onConnection(true), null);
      return {
        follow: (ids) => inner?.follow(ids.filter((id) => !isTourRun(id))),
        close: () => {
          listeners.delete(onFrame);
          inner?.close();
        },
      };
    },
  };
}
